import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HymieWidget, type AssistantConfig, type HymieStrings } from '@lopettia/hymie';
import { apiClient } from '@/lib/apiClient';
import { useAuthStore } from '@/stores/authStore';

/**
 * In-app assistant — epic 113.5. The bespoke epic-112 widget/hook is
 * retired; this mounts the off-the-shelf @lopettia/hymie HymieWidget
 * (SSE streaming with JSON fallback, inline confirm/cancel card,
 * tools-used indicator, feedback) against Kairos's Hymie
 * CONTRACT_VERSION 1.0.0 backend. No component code is copied — Hymie
 * is product- and locale-agnostic; Kairos owns auth + i18n.
 *
 * Enablement is still host-owned: we self-hide unless
 * GET /ai/assistant/status reports a configured provider (112.16).
 */
export function AssistantWidget() {
  const { t } = useTranslation();
  const [available, setAvailable] = useState(false);

  useEffect(() => {
    apiClient
      .get<{ available: boolean }>('/ai/assistant/status')
      .then((r) => setAvailable(r.data.available))
      .catch(() => setAvailable(false));
  }, []);

  const config: AssistantConfig = useMemo(
    () => ({
      endpointBase: '/api/v1/ai',
      // Auth stays Kairos's — Hymie never sees the token store.
      getAuthToken: () => useAuthStore.getState().tokens?.accessToken ?? '',
    }),
    [],
  );

  const strings: Partial<HymieStrings> = useMemo(
    () => ({
      launcher: t('assistant.open'),
      title: t('assistant.title'),
      placeholder: t('assistant.placeholder'),
      send: t('assistant.send'),
      thinking: t('assistant.thinking'),
      toolsUsed: t('assistant.tools_used'),
      confirmTitle: t('assistant.confirm_title'),
      confirm: t('assistant.confirm'),
      cancel: t('assistant.cancel'),
      feedbackUp: t('assistant.thumbs_up'),
      feedbackDown: t('assistant.thumbs_down'),
      feedbackThanks: t('assistant.feedback_thanks'),
      errorGeneric: t('assistant.error'),
      rateLimited: t('assistant.rate_limited'),
    }),
    [t],
  );

  if (!available) return null;
  return <HymieWidget config={config} strings={strings} />;
}
